import React from 'react';

export type ProgramSlideTextAlign = 'justify' | 'left' | 'center' | 'right';

export const PROGRAM_SLIDE_TEXT_ALIGN_OPTIONS: ProgramSlideTextAlign[] = [
  'justify',
  'left',
  'center',
  'right',
];

export type ProgramSlideTitleTabId = 'title' | 'criteria' | 'learningMemo' | 'evaluation';

export const DEFAULT_FONT_SIZE_PT_BY_TAB: Record<ProgramSlideTitleTabId, number> = {
  title: 60,
  criteria: 40,
  learningMemo: 40,
  evaluation: 40,
};

export interface ProgramSlideTextDisplayPrefs {
  fontSizePtByTab: Record<ProgramSlideTitleTabId, number>;
  textAlign: ProgramSlideTextAlign;
}

const FONT_MIN_PT = 8;
const FONT_MAX_PT = 120;
const DEFAULT_TEXT_ALIGN: ProgramSlideTextAlign = 'center';

const STORAGE_PREFIX_V3 = 'cp.programSlideTitleTabDisplay.v3:';
const STORAGE_PREFIX_V2 = 'cp.programSlideTitleTabDisplay.v2:';
const STORAGE_PREFIX_V1 = 'cp.programSlideTitleTabDisplay.v1:';

const TAB_IDS: ProgramSlideTitleTabId[] = ['title', 'criteria', 'learningMemo', 'evaluation'];

function defaultFontSizePtByTab(): Record<ProgramSlideTitleTabId, number> {
  return { ...DEFAULT_FONT_SIZE_PT_BY_TAB };
}

function clampFontSizePt(value: number, tabId: ProgramSlideTitleTabId): number {
  const fallback = DEFAULT_FONT_SIZE_PT_BY_TAB[tabId];
  return Math.min(FONT_MAX_PT, Math.max(FONT_MIN_PT, Math.round(value) || fallback));
}

function parseTextAlign(raw: unknown): ProgramSlideTextAlign {
  if (
    raw === 'justify' ||
    raw === 'left' ||
    raw === 'center' ||
    raw === 'right'
  ) {
    return raw;
  }
  return DEFAULT_TEXT_ALIGN;
}

export function programSlideTextAlignLabel(align: ProgramSlideTextAlign): string {
  if (align === 'justify') return '양쪽 정렬';
  if (align === 'left') return '왼쪽 정렬';
  if (align === 'center') return '가운데 정렬';
  return '오른쪽 정렬';
}

function parseFontSizePtByTab(raw: unknown): Record<ProgramSlideTitleTabId, number> {
  const out = defaultFontSizePtByTab();
  if (!raw || typeof raw !== 'object') return out;
  const o = raw as Record<string, unknown>;
  for (const tabId of TAB_IDS) {
    const n = Number(o[tabId]);
    if (Number.isFinite(n)) out[tabId] = clampFontSizePt(n, tabId);
  }
  return out;
}

export function loadProgramSlideTextDisplayPrefs(storageKey: string | undefined): ProgramSlideTextDisplayPrefs {
  const key = (storageKey ?? '').trim();
  if (!key) {
    return { fontSizePtByTab: defaultFontSizePtByTab(), textAlign: DEFAULT_TEXT_ALIGN };
  }
  try {
    const rawV3 = window.localStorage.getItem(STORAGE_PREFIX_V3 + key);
    if (rawV3) {
      const o = JSON.parse(rawV3) as { fontSizePtByTab?: unknown; textAlign?: unknown };
      return {
        fontSizePtByTab: parseFontSizePtByTab(o.fontSizePtByTab),
        textAlign: parseTextAlign(o.textAlign),
      };
    }
    const rawV2 = window.localStorage.getItem(STORAGE_PREFIX_V2 + key);
    if (rawV2) {
      const o = JSON.parse(rawV2) as { fontSizePtByTab?: unknown; textAlign?: unknown };
      return {
        fontSizePtByTab: defaultFontSizePtByTab(),
        textAlign: parseTextAlign(o.textAlign),
      };
    }
    const rawV1 = window.localStorage.getItem(STORAGE_PREFIX_V1 + key);
    if (rawV1) {
      const o = JSON.parse(rawV1) as { fontSizePx?: unknown; textAlign?: unknown };
      const textAlign = parseTextAlign(o.textAlign);
      return { fontSizePtByTab: defaultFontSizePtByTab(), textAlign };
    }
  } catch {
    /* ignore */
  }
  return { fontSizePtByTab: defaultFontSizePtByTab(), textAlign: DEFAULT_TEXT_ALIGN };
}

export function saveProgramSlideTextDisplayPrefs(
  storageKey: string | undefined,
  prefs: ProgramSlideTextDisplayPrefs
): void {
  const key = (storageKey ?? '').trim();
  if (!key) return;
  const fontSizePtByTab = defaultFontSizePtByTab();
  for (const tabId of TAB_IDS) {
    fontSizePtByTab[tabId] = clampFontSizePt(prefs.fontSizePtByTab[tabId], tabId);
  }
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX_V3 + key,
      JSON.stringify({
        fontSizePtByTab,
        textAlign: parseTextAlign(prefs.textAlign),
      })
    );
  } catch {
    /* ignore */
  }
}

export const PROGRAM_SLIDE_TEXT_ALIGN_ICONS: Record<ProgramSlideTextAlign, React.ReactNode> = {
  justify: <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />,
  left: <path d="M4 6h16v2H4V6zm0 5h11v2H4v-2zm0 5h16v2H4v-2z" />,
  center: <path d="M4 6h16v2H4V6zm3 5h10v2H7v-2zm-3 5h16v2H4v-2z" />,
  right: <path d="M4 6h16v2H4V6zm5 5h11v2H9v-2zM4 16h16v2H4v-2z" />,
};

export interface ProgramSlideTextDisplayToolbarProps {
  fontSizePt: number;
  textAlign: ProgramSlideTextAlign;
  onFontSizePtChange: (next: number) => void;
  onTextAlignChange: (next: ProgramSlideTextAlign) => void;
  className?: string;
}

/** 편집모드 개체 추가 버튼과 같은 반투명 톤 — 둥근 사각형, 감싸는 블록 없음 */
const ProgramSlideTextDisplayToolbar: React.FC<ProgramSlideTextDisplayToolbarProps> = ({
  fontSizePt,
  textAlign,
  onFontSizePtChange,
  onTextAlignChange,
  className,
}) => {
  const clamped = Math.min(FONT_MAX_PT, Math.max(FONT_MIN_PT, Math.round(fontSizePt) || FONT_MIN_PT));
  const wrapClass =
    'cp-program-slide-title-tabs-toolbar' + (className?.trim() ? ` ${className.trim()}` : '');

  return (
    <div className={wrapClass} onClick={(e) => e.stopPropagation()}>
      <div className="cp-program-slide-title-ctl-group" role="group" aria-label="텍스트 크기">
        <div className="cp-program-slide-title-ctl cp-program-slide-title-ctl--size-field">
          <span className="cp-program-slide-title-ctl-size-label">크기</span>
          <input
            type="number"
            className="cp-program-slide-title-ctl-size-input"
            min={FONT_MIN_PT}
            max={FONT_MAX_PT}
            value={clamped}
            onChange={(e) =>
              onFontSizePtChange(
                Math.min(FONT_MAX_PT, Math.max(FONT_MIN_PT, Math.round(Number(e.target.value)) || FONT_MIN_PT))
              )
            }
            aria-label="텍스트 크기(pt)"
          />
          <span className="cp-program-slide-title-ctl-size-unit" aria-hidden>
            pt
          </span>
        </div>
        <button
          type="button"
          className="cp-program-slide-title-ctl cp-program-slide-title-ctl--icon"
          onClick={() => onFontSizePtChange(clamped - 2)}
          aria-label="텍스트 크기 줄이기"
          title="텍스트 크기 줄이기"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 11h14v2H5v-2z" />
          </svg>
        </button>
        <button
          type="button"
          className="cp-program-slide-title-ctl cp-program-slide-title-ctl--icon"
          onClick={() => onFontSizePtChange(clamped + 2)}
          aria-label="텍스트 크기 키우기"
          title="텍스트 크기 키우기"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
          </svg>
        </button>
      </div>
      <div className="cp-program-slide-title-ctl-group" role="group" aria-label="텍스트 정렬">
        {PROGRAM_SLIDE_TEXT_ALIGN_OPTIONS.map((align) => (
          <button
            key={align}
            type="button"
            className={
              'cp-program-slide-title-ctl cp-program-slide-title-ctl--icon' +
              (textAlign === align ? ' is-on' : '')
            }
            onClick={() => onTextAlignChange(align)}
            aria-label={programSlideTextAlignLabel(align)}
            title={programSlideTextAlignLabel(align)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              {PROGRAM_SLIDE_TEXT_ALIGN_ICONS[align]}
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProgramSlideTextDisplayToolbar;
